import { en } from './en.js';

/**
 * One dictionary, one language, one accessor.
 *
 * ── WHY NOT react-i18next ─────────────────────────────────────────────────
 *
 * The requirement today is English only. A full i18n runtime brings a provider,
 * a loader, a plural-rule engine and a bundle, to solve problems this product
 * does not have yet. What it DOES need is the discipline: no string built by
 * concatenation, no copy inline in a component, named interpolation, and plurals
 * declared rather than hand-branched.
 *
 * So this is a typed dictionary with those rules enforced, shaped so that
 * swapping in a real library later is mechanical rather than a rewrite: the keys
 * are already namespaced, the params are already named, and no call site does
 * its own string assembly.
 *
 * ── WHY EVERY STRING MOVES, INCLUDING THE SHORT ONES ──────────────────────
 *
 * "Clear", "Cancel", "Nothing to show" scattered across forty files is how a
 * product ends up with Cancel, Close and Discard on three dialogs that do the
 * same thing. The dictionary is the only place the product's vocabulary can be
 * read in one sitting and made consistent.
 */

type Primitive = string | { one: string; other: string };

/** Dot-separated paths into the dictionary, checked at compile time. */
type Path<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends Primitive
    ? `${Prefix}${K}`
    : Path<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

export type MessageKey = Path<typeof en>;

export interface Params {
  /** Selects the plural form when the entry declares one. */
  count?: number;
  [name: string]: string | number | undefined;
}

/**
 * A plural entry needs BOTH forms, not just `other`.
 *
 * `Primitive` has always said `{ one: string; other: string }` while this asked
 * only for `other`, so a group of messages containing one called "Other" — an
 * entirely ordinary label for the last item in a list of kinds — read as a
 * plural entry.
 *
 * Leaf lookups were unaffected, because this only tests the node a key lands
 * on. What it changes is `t('some.group')` pointed at such a group: it used to
 * return the whole object as though it were a message, and now returns
 * undefined, which renders the key. The dictionary guard used the same
 * `other`-only test and did break, stopping its walk at the group and reporting
 * all six real keys beneath it as undefined.
 *
 * Fixed here rather than by renaming the offending message, because the next
 * "Other" would have hit it again.
 */
function isPlural(node: object): boolean {
  return 'one' in node && 'other' in node;
}

function lookup(key: string): Primitive | undefined {
  let node: unknown = en;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' || (typeof node === 'object' && node !== null && isPlural(node))
    ? (node as Primitive)
    : undefined;
}

/**
 * Resolve a message.
 *
 * A missing key returns the KEY, not an empty string. A blank label looks like a
 * loading state and gets shipped; a literal `journal.title` on screen does not.
 */
export function t(key: MessageKey, params?: Params): string {
  const entry = lookup(key);
  if (entry === undefined) return key;

  const template =
    typeof entry === 'string'
      ? entry
      : // English has two forms. A language with more is exactly the point at
        // which this file is replaced by a real plural-rule engine.
        (params?.count ?? 0) === 1
        ? entry.one
        : entry.other;

  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}
